<script setup>
  import { computed, onMounted } from "vue";
  import { useStore } from "vuex";

  /* =========================================
   LUCIDE ICONS
  ========================================= */
  import {
    LayoutDashboard,
    Settings,
    Palette,
    Images,
    School,
    Users,
    GraduationCap,
    Building2,
    ImageIcon,
    CalendarDays,
    Newspaper,
    MessageSquareQuote,
    Search,
    Share2,
    Phone,
    Globe,
    X,
    Sparkles,
    ChevronLeft,
    ChevronRight,
  } from "lucide-vue-next";

  /* =========================================
   PROPS & EMITS
  ========================================= */
  const props = defineProps({
    activePage: {
      type: String,
      default: "dashboard",
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
    isMobileOpen: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["change-page", "toggle-collapse", "close-mobile"]);

  /* =========================================
   STORE & DATA
  ========================================= */
  const store = useStore();

  onMounted(async () => {
    try {
      if (!store.state.websiteDetails) {
        await store.dispatch("fetchWebsiteDetails");
      }
    } catch (err) {
      console.error("Failed to fetch website details in sidebar:", err);
    }
  });

  const websiteDetails = computed(
    () => store.state.websiteDetails || store.getters["getWebsiteDetails"] || null,
  );
  const schoolData = computed(() => store.state.schools?.[0] || null);

  const schoolName = computed(
    () => websiteDetails.value?.schoolName || schoolData.value?.schoolName || "Educanium School",
  );
  const schoolSlug = computed(
    () => websiteDetails.value?.slug || schoolData.value?.slug || "portal",
  );
  const isLive = computed(
    () => websiteDetails.value?.websiteEnabled ?? schoolData.value?.websiteEnabled ?? true,
  );

  /* =========================================
   NAVIGATION GROUPS
  ========================================= */
  const navGroups = [
    {
      label: "Overview & Studio",
      shortLabel: "Core",
      items: [
        {
          to: "dashboard",
          label: "Studio Command Center",
          shortLabel: "Overview",
          icon: LayoutDashboard,
        },
        {
          to: "website-settings",
          label: "Branding & Details",
          shortLabel: "Settings",
          icon: Settings,
        },
        {
          to: "theme-settings",
          label: "Theme & Visual Identity",
          shortLabel: "Themes",
          icon: Palette,
        },
      ],
    },
    {
      label: "Core Content & Story",
      shortLabel: "Content",
      items: [
        {
          to: "hero-section",
          label: "Hero Banner & Carousel",
          shortLabel: "Hero Banner",
          icon: Images,
        },
        {
          to: "about-school",
          label: "About School & History",
          shortLabel: "About",
          icon: School,
        },
        {
          to: "leadership",
          label: "Principal & Leadership",
          shortLabel: "Leadership",
          icon: Users,
        },
        {
          to: "academics",
          label: "Academic Programs",
          shortLabel: "Academics",
          icon: GraduationCap,
        },
        {
          to: "facilities",
          label: "Facilities & Amenities",
          shortLabel: "Facilities",
          icon: Building2,
        },
      ],
    },
    {
      label: "Media & Community",
      shortLabel: "Media",
      items: [
        {
          to: "gallery",
          label: "Campus Photo & Video Gallery",
          shortLabel: "Gallery",
          icon: ImageIcon,
        },
        {
          to: "events",
          label: "Events & Annual Calendar",
          shortLabel: "Events",
          icon: CalendarDays,
        },
        {
          to: "notifications",
          label: "Circulars & Notices",
          shortLabel: "Notices",
          icon: Newspaper,
        },
        {
          to: "campus-infrastructure",
          label: "Campus Infrastructure",
          shortLabel: "Campus",
          icon: MessageSquareQuote,
        },
      ],
    },
    {
      label: "Configuration & Reach",
      shortLabel: "Config",
      items: [
        {
          to: "seo-settings",
          label: "SEO & Social OpenGraph",
          shortLabel: "SEO & Meta",
          icon: Search,
        },
        {
          to: "social-links",
          label: "Prospectus & Footer",
          shortLabel: "Prospectus",
          icon: Share2,
        },
        {
          to: "contact-details",
          label: "Contact & Location",
          shortLabel: "Contact",
          icon: Phone,
        },
        {
          to: "domain-settings",
          label: "Custom Domain & SSL",
          shortLabel: "Domain",
          badge: "Live",
          icon: Globe,
        },
      ],
    },
  ];

  /* =========================================
   ACTIONS
  ========================================= */
  const selectPage = (pageKey) => {
    emit("change-page", pageKey);
    emit("close-mobile");
  };

  const handleToggleCollapse = () => {
    emit("toggle-collapse");
  };

  const handleCloseMobile = () => {
    emit("close-mobile");
  };
</script>

<template>
  <!-- ======================================================== -->
  <!-- 1. MOBILE SLIDE-OVER DRAWER (Visible on < lg screens) -->
  <!-- ======================================================== -->
  <teleport to="body">
    <transition name="drawer-fade">
      <div
        v-if="props.isMobileOpen"
        class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[9998] lg:hidden"
        @click="handleCloseMobile"
      />
    </transition>

    <transition name="drawer-slide">
      <aside
        v-if="props.isMobileOpen"
        class="fixed top-3 left-3 bottom-3 w-[64vw] max-w-[215px] bg-white/95 dark:bg-[#0F172A]/95 backdrop-blur-2xl z-[9999] shadow-2xl shadow-indigo-950/25 rounded-2xl flex flex-col border border-slate-200/90 dark:border-[#263449] ring-1 ring-black/5 dark:ring-white/10 overflow-hidden lg:hidden select-none"
      >
        <!-- Mobile Drawer Header -->
        <div
          class="px-3 py-2.5 border-b border-slate-100 dark:border-[#263449] bg-gradient-to-r from-slate-50/90 via-indigo-50/30 to-purple-50/20 dark:from-[#18243A]/80 dark:via-[#152033] dark:to-[#111C30] flex items-center justify-between gap-2"
        >
          <div class="flex items-center gap-2 min-w-0">
            <div
              class="w-6.5 h-6.5 rounded-lg bg-gradient-to-tr from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center text-white shadow-xs ring-1 ring-white/20 shrink-0"
            >
              <Sparkles :size="12" />
            </div>
            <div class="min-w-0">
              <h3 class="text-[11.5px] font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate leading-tight">
                Studio Palette
              </h3>
              <p class="text-[8.5px] font-bold uppercase tracking-wider text-indigo-600 dark:text-blue-400 truncate">
                CMS Navigator
              </p>
            </div>
          </div>

          <button
            type="button"
            class="w-6 h-6 rounded-lg bg-slate-100 dark:bg-[#18243A] text-slate-400 hover:text-slate-700 dark:hover:text-white flex items-center justify-center transition-all cursor-pointer active:scale-90 shrink-0 border border-slate-200/60 dark:border-[#263449]/70"
            @click="handleCloseMobile"
            aria-label="Close menu"
          >
            <X :size="13" />
          </button>
        </div>

        <!-- Mobile Drawer Nav List -->
        <nav class="flex-1 overflow-y-auto px-2 py-2 space-y-2.5 custom-scrollbar">
          <div
            v-for="group in navGroups"
            :key="group.label"
            class="space-y-0.5"
          >
            <p
              class="px-2 pt-1 pb-0.5 text-[8.5px] font-black uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5"
            >
              <span class="w-1 h-1 rounded-full bg-indigo-500/70 dark:bg-blue-400/70 shrink-0"></span>
              <span class="truncate">{{ group.shortLabel || group.label }}</span>
            </p>

            <button
              v-for="item in group.items"
              :key="item.to"
              type="button"
              :class="[
                'group relative w-full flex items-center gap-2 px-2 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer text-left',
                props.activePage === item.to
                  ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-indigo-700 text-white shadow-xs shadow-indigo-500/25 font-bold ring-1 ring-white/15'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] hover:bg-slate-100/80 dark:hover:bg-[#18243A]',
              ]"
              @click="selectPage(item.to)"
            >
              <div
                class="w-5.5 h-5.5 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                :class="props.activePage === item.to ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5] group-hover:text-blue-600 dark:group-hover:text-blue-400'"
              >
                <component
                  :is="item.icon"
                  :size="12.5"
                  :stroke-width="props.activePage === item.to ? 2.4 : 1.8"
                />
              </div>

              <span class="truncate flex-1 tracking-tight text-[11px]">
                {{ item.shortLabel || item.label }}
              </span>

              <span
                v-if="item.badge"
                :class="[
                  'px-1.5 py-0.2 text-[8px] font-black rounded-full uppercase tracking-wider shrink-0',
                  props.activePage === item.to
                    ? 'bg-white/25 text-white'
                    : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40',
                ]"
              >
                {{ item.badge }}
              </span>

              <!-- Active jewel dot on right -->
              <span
                v-if="props.activePage === item.to && !item.badge"
                class="w-1.5 h-1.5 rounded-full bg-white shrink-0"
              />
            </button>
          </div>
        </nav>

        <!-- Mobile Drawer Mini Footer -->
        <div
          class="px-2.5 py-2 border-t border-slate-100 dark:border-[#263449] bg-gradient-to-r from-slate-50/80 to-indigo-50/20 dark:from-[#18243A]/40 dark:to-[#111C30]/40 flex items-center justify-between gap-2 shrink-0"
        >
          <div class="min-w-0 flex-1">
            <p class="text-[9.5px] font-bold text-slate-800 dark:text-[#F1F5F9] truncate leading-tight">
              {{ schoolName }}
            </p>
            <p class="text-[8.5px] font-mono text-slate-400 dark:text-[#7E8DA5] truncate">
              {{ schoolSlug }}.educanium.com
            </p>
          </div>

          <span
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8.5px] font-bold shrink-0"
            :class="isLive
              ? 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-400 border border-emerald-300/80 dark:border-emerald-800/40'
              : 'bg-amber-100 dark:bg-amber-950/70 text-amber-700 dark:text-amber-400 border border-amber-300/80 dark:border-amber-800/40'"
          >
            <span
              class="w-1.5 h-1.5 rounded-full"
              :class="isLive ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'"
            />
            <span>{{ isLive ? 'Live' : 'Draft' }}</span>
          </span>
        </div>
      </aside>
    </transition>
  </teleport>

  <!-- ======================================================== -->
  <!-- 2. COMPACT STUDIO PALETTE SIDEBAR (Desktop lg+ screens)  -->
  <!-- ======================================================== -->
  <aside
    :class="[
      'hidden lg:flex flex-col h-full bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] rounded-2xl shadow-xs transition-all duration-300 select-none overflow-hidden shrink-0',
      props.isCollapsed ? 'w-16' : 'w-52 xl:w-56',
    ]"
  >
    <!-- Studio Header / Mini Identity & Toggle -->
    <div
      class="px-3 py-3 border-b border-slate-100 dark:border-[#263449] bg-gradient-to-b from-slate-50/60 dark:from-[#18243A]/40 to-transparent flex items-center justify-between gap-2"
    >
      <div
        v-if="!props.isCollapsed"
        class="flex items-center gap-2 min-w-0"
      >
        <div class="w-7 h-7 rounded-lg bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shrink-0 shadow-xs">
          <Sparkles :size="13" />
        </div>
        <div class="min-w-0">
          <h3 class="text-xs font-black text-slate-900 dark:text-[#F1F5F9] truncate leading-tight">
            Studio Palette
          </h3>
          <p class="text-[9.5px] font-mono text-slate-400 dark:text-[#7E8DA5] truncate">
            Website Builder
          </p>
        </div>
      </div>

      <!-- Collapsed Studio Icon -->
      <div
        v-else
        class="w-full flex justify-center"
      >
        <div class="w-8 h-8 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-xs">
          <Sparkles :size="15" />
        </div>
      </div>

      <!-- Quick Collapse / Expand button in header -->
      <button
        v-if="!props.isCollapsed"
        type="button"
        @click="handleToggleCollapse"
        class="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#18243A] transition cursor-pointer shrink-0"
        title="Collapse studio palette"
      >
        <ChevronLeft :size="16" />
      </button>
    </div>

    <!-- Scrollable Navigation Hubs -->
    <nav class="flex-1 overflow-y-auto px-2 py-2.5 space-y-3.5 custom-scrollbar">
      <div
        v-for="group in navGroups"
        :key="group.label"
        class="space-y-0.5"
      >
        <!-- Category Title (Expanded Only) -->
        <p
          v-if="!props.isCollapsed"
          class="px-2 pt-1 pb-0.5 text-[9.5px] font-black uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]"
        >
          {{ group.shortLabel || group.label }}
        </p>

        <!-- Divider (Collapsed Only) -->
        <div
          v-else
          class="w-6 h-px bg-slate-200 dark:bg-[#263449] mx-auto my-1.5"
        />

        <!-- Menu Item (Expanded) -->
        <template v-if="!props.isCollapsed">
          <button
            v-for="item in group.items"
            :key="item.to"
            type="button"
            :class="[
              'group relative w-full flex items-center gap-2 px-2 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer text-left',
              props.activePage === item.to
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xs shadow-blue-500/20 font-bold'
                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] hover:bg-slate-100/80 dark:hover:bg-[#18243A]',
            ]"
            @click="selectPage(item.to)"
          >
            <div
              class="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors"
              :class="props.activePage === item.to ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5] group-hover:text-blue-600 dark:group-hover:text-blue-400'"
            >
              <component
                :is="item.icon"
                :size="13"
                :stroke-width="props.activePage === item.to ? 2.4 : 1.8"
              />
            </div>

            <span class="truncate flex-1 tracking-tight text-[11.5px]">
              {{ item.shortLabel || item.label }}
            </span>

            <span
              v-if="item.badge"
              class="px-1.5 py-0.2 text-[8.5px] font-bold rounded-full uppercase shrink-0"
              :class="props.activePage === item.to ? 'bg-white/25 text-white' : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40'"
            >
              {{ item.badge }}
            </span>
          </button>
        </template>

        <!-- Menu Item (Collapsed Rail Mode) -->
        <template v-else>
          <button
            v-for="item in group.items"
            :key="item.to"
            type="button"
            :title="item.label"
            class="relative w-9 h-9 mx-auto rounded-xl flex items-center justify-center transition-all duration-150 cursor-pointer group mb-1"
            :class="props.activePage === item.to
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xs shadow-blue-500/25'
              : 'text-slate-500 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] hover:text-slate-900 dark:hover:text-white'"
            @click="selectPage(item.to)"
          >
            <component
              :is="item.icon"
              :size="16"
              :stroke-width="props.activePage === item.to ? 2.4 : 1.8"
            />

            <!-- Left indicator dot for collapsed mode -->
            <div
              v-if="props.activePage === item.to"
              class="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-white rounded-r-full"
            />

            <!-- Hover Tooltip -->
            <span
              class="absolute left-full ml-2.5 px-2.5 py-1 bg-slate-900 dark:bg-slate-800 text-white text-[11px] font-semibold rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 border border-slate-700"
            >
              {{ item.label }}
            </span>
          </button>
        </template>
      </div>
    </nav>

    <!-- Bottom Status / Collapse Toggle -->
    <div
      class="px-2.5 py-2 border-t border-slate-100 dark:border-[#263449] bg-slate-50/60 dark:bg-[#18243A]/30 flex items-center"
      :class="props.isCollapsed ? 'justify-center' : 'justify-between'"
    >
      <div
        v-if="!props.isCollapsed"
        class="flex items-center gap-1.5 text-[10px] font-medium text-slate-500 dark:text-[#A6B4C8] px-1 truncate"
      >
        <span
          class="w-1.5 h-1.5 rounded-full shrink-0"
          :class="isLive ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'"
        />
        <span class="truncate">{{ isLive ? "Portal Live" : "Staging" }}</span>
      </div>

      <button
        type="button"
        :title="props.isCollapsed ? 'Expand Studio Palette' : 'Collapse Studio Palette'"
        class="p-1 rounded-lg text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-[#18243A] transition cursor-pointer"
        @click="handleToggleCollapse"
      >
        <ChevronRight v-if="props.isCollapsed" :size="15" />
        <ChevronLeft v-else :size="15" />
      </button>
    </div>
  </aside>
</template>

<style scoped>
  /* =========================================
     TRANSITIONS
  ========================================= */
  .drawer-fade-enter-active,
  .drawer-fade-leave-active {
    transition: opacity 0.25s ease;
  }
  .drawer-fade-enter-from,
  .drawer-fade-leave-to {
    opacity: 0;
  }

  .drawer-slide-enter-active,
  .drawer-slide-leave-active {
    transition: transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .drawer-slide-enter-from,
  .drawer-slide-leave-to {
    transform: translateX(-110%);
  }

  /* =========================================
     CUSTOM SCROLLBAR
  ========================================= */
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(203, 213, 225, 0.5);
    border-radius: 9999px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.8);
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(38, 52, 73, 0.8);
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #3b82f6;
  }
</style>
